import { createSlice, createAsyncThunk } from '@reduxjs/toolkit';
import axiosClient from '../axios/api.js';

// Thunks backing the `targetcompany/:id` route (TargetCompanyDetails) and its
// child dialogs.

const asMessage = (error, fallback) =>
  error.response?.data?.error ||
  error.response?.data?.message ||
  error.message ||
  fallback;

// Both the LinkedIn and telecaller endpoints return either `{ data, total }` or
// a bare array depending on the query, so normalise once here.
const normalizeList = (data) => {
  if (data && Array.isArray(data.data)) {
    return { data: data.data, total: data.total };
  }
  if (Array.isArray(data)) {
    return { data, total: data.length };
  }
  return { data: [], total: 0 };
};

// --------------------------------------------------------- target company

export const fetchTargetCompany = createAsyncThunk(
  'targetCompany/fetchTargetCompany',
  async (id, { rejectWithValue }) => {
    try {
      const response = await axiosClient.get(`/api/targetcompany/${id}`);
      return response.data;
    } catch (error) {
      return rejectWithValue(asMessage(error, 'Error fetching company details'));
    }
  }
);

export const fetchEmailStats = createAsyncThunk(
  'targetCompany/fetchEmailStats',
  async (companyName, { rejectWithValue }) => {
    try {
      const response = await axiosClient.get(
        `/api/email-stats/${encodeURIComponent(companyName)}`
      );
      return response.data;
    } catch (error) {
      return rejectWithValue(asMessage(error, 'Error fetching email stats'));
    }
  }
);

// Loads the company, then its email stats plus both address lists in parallel —
// the page previously did this inline so the initial spinner covered all four.
export const loadTargetCompanyPage = createAsyncThunk(
  'targetCompany/loadTargetCompanyPage',
  async (id, { dispatch }) => {
    const companyResult = await dispatch(fetchTargetCompany(id));
    const company = companyResult.payload;

    const tasks = [
      dispatch(fetchTargetShippingData(id)),
      dispatch(fetchTargetBillingData(id)),
    ];
    if (company?.company_name) {
      tasks.push(dispatch(fetchEmailStats(company.company_name)));
    }
    await Promise.all(tasks);
    return true;
  }
);

export const updateTargetCompanyOverview = createAsyncThunk(
  'targetCompany/updateTargetCompanyOverview',
  async ({ id, company }, { rejectWithValue }) => {
    try {
      const response = await axiosClient.put('/api/targetcompanyoverview/', { id, company });
      return response.data;
    } catch (error) {
      return rejectWithValue(asMessage(error, 'Unable to edit Company Overview'));
    }
  }
);

export const updateTargetCompanyEmail = createAsyncThunk(
  'targetCompany/updateTargetCompanyEmail',
  async ({ company_email, id }, { dispatch, rejectWithValue }) => {
    try {
      const response = await axiosClient.post('/api/targetcompanyemail/', { company_email, id });
      dispatch(fetchTargetCompany(id));
      return response.data;
    } catch (error) {
      return rejectWithValue(asMessage(error, 'Unable to edit Company Email. Please try again.'));
    }
  }
);

export const addTargetCompanyCategory = createAsyncThunk(
  'targetCompany/addTargetCompanyCategory',
  async ({ id, category }, { dispatch, rejectWithValue }) => {
    try {
      const response = await axiosClient.post('/api/targetcompanycategoryadd/', { id, category });
      dispatch(fetchTargetCompany(id));
      return response.data;
    } catch (error) {
      return rejectWithValue(asMessage(error, 'Unable to add Client Category. Please try again.'));
    }
  }
);

export const editTargetCompanyCategory = createAsyncThunk(
  'targetCompany/editTargetCompanyCategory',
  async ({ id, category }, { dispatch, rejectWithValue }) => {
    try {
      const response = await axiosClient.put('/api/targetcompanycategoryedit/', { id, category });
      dispatch(fetchTargetCompany(id));
      return response.data;
    } catch (error) {
      return rejectWithValue(asMessage(error, 'Unable to edit Client Category. Please try again.'));
    }
  }
);

export const addTargetCompanyHandledBy = createAsyncThunk(
  'targetCompany/addTargetCompanyHandledBy',
  async ({ id, handledBy }, { dispatch, rejectWithValue }) => {
    try {
      const response = await axiosClient.post('/api/targetcompanyhandledbyadd/', { id, handledBy });
      dispatch(fetchTargetCompany(id));
      return response.data;
    } catch (error) {
      return rejectWithValue(asMessage(error, 'Unable to update Handle By. Please try again.'));
    }
  }
);

export const editTargetCompanyHandledBy = createAsyncThunk(
  'targetCompany/editTargetCompanyHandledBy',
  async ({ id, handledBy }, { dispatch, rejectWithValue }) => {
    try {
      const response = await axiosClient.put('/api/targetcompanyhandledbyedit/', { id, handledBy });
      dispatch(fetchTargetCompany(id));
      return response.data;
    } catch (error) {
      return rejectWithValue(asMessage(error, 'Unable to edit Handle By. Please try again.'));
    }
  }
);

export const moveTargetCompany = createAsyncThunk(
  'targetCompany/moveTargetCompany',
  async ({ id, payload }, { rejectWithValue }) => {
    try {
      const response = await axiosClient.put(`/api/company/move/${id}`, payload);
      return response.data;
    } catch (error) {
      return rejectWithValue(asMessage(error, 'Failed to move company'));
    }
  }
);

export const fetchTargetData = createAsyncThunk(
  'targetCompany/fetchTargetData',
  async (_, { rejectWithValue }) => {
    try {
      const response = await axiosClient.get('/targetdata');
      return response.data;
    } catch (error) {
      return rejectWithValue(asMessage(error, 'Failed to fetch target data'));
    }
  }
);

// ------------------------------------------------------------- LinkedIn

export const fetchLinkedinData = createAsyncThunk(
  'targetCompany/fetchLinkedinData',
  async ({ id, limit, offset, search }, { rejectWithValue }) => {
    try {
      const response = await axiosClient.get(`/api/linkedindata/${id}`, {
        params: { limit, offset, search },
      });
      return normalizeList(response.data);
    } catch (error) {
      return rejectWithValue(asMessage(error, 'Failed to fetch LinkedIn contacts'));
    }
  }
);

export const searchLinkedinData = createAsyncThunk(
  'targetCompany/searchLinkedinData',
  async ({ id, q, limit, offset }, { rejectWithValue }) => {
    try {
      const response = await axiosClient.get(`/api/linkedindata/${id}/search`, {
        params: { q, limit, offset },
      });
      return normalizeList(response.data);
    } catch (error) {
      return rejectWithValue(asMessage(error, 'LinkedIn search error'));
    }
  }
);

export const addLinkedinContact = createAsyncThunk(
  'targetCompany/addLinkedinContact',
  async ({ id, payload }, { rejectWithValue }) => {
    try {
      const response = await axiosClient.post(`/api/company/addnewlinkedin/${id}`, payload);
      return response.data;
    } catch (error) {
      return rejectWithValue(asMessage(error, 'Adding LinkedIn contact error'));
    }
  }
);

export const fetchLinkedinToEdit = createAsyncThunk(
  'targetCompany/fetchLinkedinToEdit',
  async (targetlinkedindetails_id, { rejectWithValue }) => {
    try {
      const response = await axiosClient.get(`/api/toeditlinkedindata/${targetlinkedindetails_id}`);
      return response.data;
    } catch (error) {
      return rejectWithValue(asMessage(error, 'Failed to fetch LinkedIn contact'));
    }
  }
);

export const updateLinkedinDetails = createAsyncThunk(
  'targetCompany/updateLinkedinDetails',
  async ({ id, payload }, { rejectWithValue }) => {
    try {
      const response = await axiosClient.put(`/api/editlinkedincompany/${id}`, payload);
      return response.data;
    } catch (error) {
      return rejectWithValue(asMessage(error, 'Error updating LinkedIn details'));
    }
  }
);

export const updateLinkedinContact = createAsyncThunk(
  'targetCompany/updateLinkedinContact',
  async ({ id, payload }, { rejectWithValue }) => {
    try {
      const response = await axiosClient.put(`/api/linkedindata/${id}`, payload);
      return response.data;
    } catch (error) {
      return rejectWithValue(asMessage(error, 'Edit LinkedIn contact error'));
    }
  }
);

export const deleteLinkedinContact = createAsyncThunk(
  'targetCompany/deleteLinkedinContact',
  async (contactId, { rejectWithValue }) => {
    try {
      await axiosClient.delete(`/api/linkedincontacts/${contactId}`);
      return contactId;
    } catch (error) {
      if (error.response?.status === 409) {
        return rejectWithValue({ conflict: true, ...error.response.data, contactId });
      }
      return rejectWithValue({ message: asMessage(error, 'Delete LinkedIn contact error') });
    }
  }
);

export const toggleLinkedinSubscription = createAsyncThunk(
  'targetCompany/toggleLinkedinSubscription',
  async ({ contactId, subscribed }, { rejectWithValue }) => {
    try {
      const response = await axiosClient.put(`/api/linkedindata/${contactId}/subscribed`, {
        subscribed,
      });
      return { contactId, subscribed, data: response.data };
    } catch (error) {
      return rejectWithValue(asMessage(error, 'Failed to update subscription'));
    }
  }
);

export const uploadLinkedinContacts = createAsyncThunk(
  'targetCompany/uploadLinkedinContacts',
  async (formData, { rejectWithValue }) => {
    try {
      const response = await axiosClient.post('/linkedinupload', formData, {
        headers: { 'Content-Type': 'multipart/form-data' },
      });
      return response.data;
    } catch (error) {
      return rejectWithValue(asMessage(error, 'File upload error'));
    }
  }
);

export const moveToTelecaller = createAsyncThunk(
  'targetCompany/moveToTelecaller',
  async (contactId, { rejectWithValue }) => {
    try {
      const response = await axiosClient.post(`/api/movetotelecaller/${contactId}`);
      return response.data;
    } catch (error) {
      return rejectWithValue(asMessage(error, 'Failed to move contact to telecaller'));
    }
  }
);

export const moveToTelecallerBulk = createAsyncThunk(
  'targetCompany/moveToTelecallerBulk',
  async (ids, { rejectWithValue }) => {
    try {
      const response = await axiosClient.post('/api/movetotelecaller/bulk', { ids });
      return response.data;
    } catch (error) {
      return rejectWithValue(asMessage(error, 'Failed to move contacts to telecaller'));
    }
  }
);

// ----------------------------------------------------------- telecaller

export const fetchTelecallerData = createAsyncThunk(
  'targetCompany/fetchTelecallerData',
  async ({ id, limit, offset, search }, { rejectWithValue }) => {
    try {
      const response = await axiosClient.get(`/api/telecallerdata/${id}`, {
        params: { limit, offset, search },
      });
      return normalizeList(response.data);
    } catch (error) {
      return rejectWithValue(asMessage(error, 'Error fetching telecaller data'));
    }
  }
);

export const searchTelecallerData = createAsyncThunk(
  'targetCompany/searchTelecallerData',
  async ({ id, q, limit, offset }, { rejectWithValue }) => {
    try {
      const response = await axiosClient.get(`/api/telecallerdata/${id}/search`, {
        params: { q, limit, offset },
      });
      return normalizeList(response.data);
    } catch (error) {
      return rejectWithValue(asMessage(error, 'Telecaller search error'));
    }
  }
);

export const addTelecallerContact = createAsyncThunk(
  'targetCompany/addTelecallerContact',
  async ({ addtelecaller, payload }, { rejectWithValue }) => {
    try {
      const response = await axiosClient.post(
        `/api/company/addnewtelecaller/${addtelecaller}`,
        payload
      );
      return response.data;
    } catch (error) {
      return rejectWithValue(asMessage(error, 'Adding telecaller contact error'));
    }
  }
);

export const fetchTelecallerToEdit = createAsyncThunk(
  'targetCompany/fetchTelecallerToEdit',
  async (targettelecallerdetails_id, { rejectWithValue }) => {
    try {
      const response = await axiosClient.get(
        `/api/toedittelecallerdata/${targettelecallerdetails_id}`
      );
      return response.data;
    } catch (error) {
      return rejectWithValue(asMessage(error, 'Failed to fetch telecaller contact'));
    }
  }
);

export const updateTelecallerDetails = createAsyncThunk(
  'targetCompany/updateTelecallerDetails',
  async ({ targettelecallerdetails_id, payload }, { rejectWithValue }) => {
    try {
      const response = await axiosClient.put(
        `/api/editedtelecallercompany/${targettelecallerdetails_id}`,
        payload
      );
      return response.data;
    } catch (error) {
      return rejectWithValue(asMessage(error, 'Error updating telecaller details'));
    }
  }
);

export const updateTelecallerContact = createAsyncThunk(
  'targetCompany/updateTelecallerContact',
  async ({ id, payload }, { rejectWithValue }) => {
    try {
      const response = await axiosClient.put(`/api/telecallerdata/${id}`, payload);
      return response.data;
    } catch (error) {
      return rejectWithValue(asMessage(error, 'Edit telecaller contact error'));
    }
  }
);

export const deleteTelecallerContact = createAsyncThunk(
  'targetCompany/deleteTelecallerContact',
  async (contactId, { rejectWithValue }) => {
    try {
      await axiosClient.delete(`/api/telecallercontacts/${contactId}`);
      return contactId;
    } catch (error) {
      if (error.response?.status === 409) {
        return rejectWithValue({ conflict: true, ...error.response.data, contactId });
      }
      return rejectWithValue({ message: asMessage(error, 'Delete telecaller contact error') });
    }
  }
);

export const toggleTelecallerSubscription = createAsyncThunk(
  'targetCompany/toggleTelecallerSubscription',
  async ({ contactId, subscribed }, { rejectWithValue }) => {
    try {
      const response = await axiosClient.put(`/api/telecallerdata/${contactId}/subscribed`, {
        subscribed,
      });
      return { contactId, subscribed, data: response.data };
    } catch (error) {
      return rejectWithValue(asMessage(error, 'Failed to update subscription'));
    }
  }
);

export const uploadTelecallerContacts = createAsyncThunk(
  'targetCompany/uploadTelecallerContacts',
  async (formData, { rejectWithValue }) => {
    try {
      const response = await axiosClient.post('/telecallerupload', formData, {
        headers: { 'Content-Type': 'multipart/form-data' },
      });
      return response.data;
    } catch (error) {
      return rejectWithValue(asMessage(error, 'File upload error'));
    }
  }
);

export const reassignAndDeleteTargetContact = createAsyncThunk(
  'targetCompany/reassignAndDeleteTargetContact',
  async (body, { rejectWithValue }) => {
    try {
      const response = await axiosClient.post('/api/target-contact-reassign-and-delete', body);
      return response.data;
    } catch (error) {
      return rejectWithValue(asMessage(error, 'Failed to reassign and delete contact'));
    }
  }
);

// ------------------------------------------------------ shipping/billing

export const fetchTargetShippingData = createAsyncThunk(
  'targetCompany/fetchTargetShippingData',
  async (id, { rejectWithValue }) => {
    try {
      const response = await axiosClient.get(`/targetshippingdata/${id}`);
      return response.data;
    } catch (error) {
      return rejectWithValue(asMessage(error, 'Failed to fetch shipping addresses'));
    }
  }
);

export const fetchTargetBillingData = createAsyncThunk(
  'targetCompany/fetchTargetBillingData',
  async (id, { rejectWithValue }) => {
    try {
      const response = await axiosClient.get(`/targetbillingdata/${id}`);
      return response.data;
    } catch (error) {
      return rejectWithValue(asMessage(error, 'Failed to fetch billing addresses'));
    }
  }
);

export const addTargetShippingAddress = createAsyncThunk(
  'targetCompany/addTargetShippingAddress',
  async ({ companynameshippingaddress, payload }, { rejectWithValue }) => {
    try {
      const response = await axiosClient.post(
        `/api/targetcompanyshippingaddress/${companynameshippingaddress}`,
        payload
      );
      return response.data;
    } catch (error) {
      return rejectWithValue(asMessage(error, 'Shipping address add error'));
    }
  }
);

export const addTargetBillingAddress = createAsyncThunk(
  'targetCompany/addTargetBillingAddress',
  async ({ companynamesbillingaddress, payload }, { rejectWithValue }) => {
    try {
      const response = await axiosClient.post(
        `/api/targetcompanybillingaddress/${companynamesbillingaddress}`,
        payload
      );
      return response.data;
    } catch (error) {
      return rejectWithValue(asMessage(error, 'Billing address add error'));
    }
  }
);

export const fetchTargetShippingToEdit = createAsyncThunk(
  'targetCompany/fetchTargetShippingToEdit',
  async (shippingid, { rejectWithValue }) => {
    try {
      const response = await axiosClient.get(`/edittargetshippingdata/${shippingid}`);
      return response.data;
    } catch (error) {
      return rejectWithValue(asMessage(error, 'Failed to fetch shipping address'));
    }
  }
);

export const fetchTargetBillingToEdit = createAsyncThunk(
  'targetCompany/fetchTargetBillingToEdit',
  async (billingid, { rejectWithValue }) => {
    try {
      const response = await axiosClient.get(`/edittargetbillingdata/${billingid}`);
      return response.data;
    } catch (error) {
      return rejectWithValue(asMessage(error, 'Failed to fetch billing address'));
    }
  }
);

export const editTargetShippingAddress = createAsyncThunk(
  'targetCompany/editTargetShippingAddress',
  async ({ editshippingaddressdata, shippingid }, { rejectWithValue }) => {
    try {
      const response = await axiosClient.put('/api/shippingedittarget', {
        editshippingaddressdata,
        shippingid,
      });
      return response.data;
    } catch (error) {
      return rejectWithValue(asMessage(error, 'Shipping address edit error'));
    }
  }
);

export const editTargetBillingAddress = createAsyncThunk(
  'targetCompany/editTargetBillingAddress',
  async ({ editbillingaddressdata, billingid }, { rejectWithValue }) => {
    try {
      const response = await axiosClient.put('/api/billingaddressedittarget', {
        editbillingaddressdata,
        billingid,
      });
      return response.data;
    } catch (error) {
      return rejectWithValue(asMessage(error, 'Billing address edit error'));
    }
  }
);

export const deleteTargetShippingAddress = createAsyncThunk(
  'targetCompany/deleteTargetShippingAddress',
  async (shippingid, { rejectWithValue }) => {
    try {
      await axiosClient.delete(`/api/companyshippingaddresstarget/${shippingid}`);
      return shippingid;
    } catch (error) {
      return rejectWithValue(asMessage(error, 'Delete Shipping address error'));
    }
  }
);

export const deleteTargetBillingAddress = createAsyncThunk(
  'targetCompany/deleteTargetBillingAddress',
  async (billingid, { rejectWithValue }) => {
    try {
      await axiosClient.delete(`/api/companybillingaddresstarget/${billingid}`);
      return billingid;
    } catch (error) {
      return rejectWithValue(asMessage(error, 'Delete Billing address error'));
    }
  }
);

export const uploadTargetShippingAddresses = createAsyncThunk(
  'targetCompany/uploadTargetShippingAddresses',
  async (formData, { rejectWithValue }) => {
    try {
      const response = await axiosClient.post('/targetshippingupload', formData, {
        headers: { 'Content-Type': 'multipart/form-data' },
      });
      return response.data;
    } catch (error) {
      return rejectWithValue(asMessage(error, 'File upload error'));
    }
  }
);

export const uploadTargetBillingAddresses = createAsyncThunk(
  'targetCompany/uploadTargetBillingAddresses',
  async (formData, { rejectWithValue }) => {
    try {
      const response = await axiosClient.post('/targetbillingupload', formData, {
        headers: { 'Content-Type': 'multipart/form-data' },
      });
      return response.data;
    } catch (error) {
      return rejectWithValue(asMessage(error, 'File upload error'));
    }
  }
);

// ------------------------------------------------------------ reference

export const fetchTargetCountryCodeOptions = createAsyncThunk(
  'targetCompany/fetchTargetCountryCodeOptions',
  async (_, { rejectWithValue }) => {
    try {
      const response = await axiosClient.get('/api/allcompanycountrydata/');
      return response.data;
    } catch (error) {
      return rejectWithValue(asMessage(error, 'Failed to fetch country codes'));
    }
  }
);

export const fetchTargetCountryDialCode = createAsyncThunk(
  'targetCompany/fetchTargetCountryDialCode',
  async (country, { rejectWithValue }) => {
    try {
      const response = await axiosClient.get(
        `/api/get-country-code?country=${encodeURIComponent(country)}`
      );
      return response.data;
    } catch (error) {
      return rejectWithValue(asMessage(error, 'Failed to fetch country dial code'));
    }
  }
);

const TEMPLATE_ENDPOINTS = {
  allContacts: '/fetch-allcontacts-documentformat',
  address: '/fetch-address-document-format',
  clients: '/fetch-clients-documentformat',
};

// Download links for the bulk-upload spreadsheet templates.
export const fetchTargetTemplateUrl = createAsyncThunk(
  'targetCompany/fetchTargetTemplateUrl',
  async (kind, { rejectWithValue }) => {
    try {
      const response = await axiosClient.get(TEMPLATE_ENDPOINTS[kind]);
      return { kind, url: response.data };
    } catch (error) {
      return rejectWithValue(asMessage(error, 'Failed to fetch template URL'));
    }
  }
);

export const fetchTelecallerLocationData = createAsyncThunk(
  'targetCompany/fetchTelecallerLocationData',
  async (companyId, { rejectWithValue }) => {
    try {
      const response = await axiosClient.get(`/telecallerlocationdata/${companyId}`);
      return response.data;
    } catch (error) {
      return rejectWithValue(asMessage(error, 'Error fetching telecaller location data'));
    }
  }
);

export const fetchLinkedinLocationData = createAsyncThunk(
  'targetCompany/fetchLinkedinLocationData',
  async (companyId, { rejectWithValue }) => {
    try {
      const response = await axiosClient.get(`/linkedinlocationdata/${companyId}`);
      return response.data;
    } catch (error) {
      return rejectWithValue(asMessage(error, 'Error fetching LinkedIn location data'));
    }
  }
);

const initialState = {
  company: {},
  companyLoading: false,
  companyError: null,
  emailStats: {
    mail: 0, drafts: 0, scheduled: 0, opened: 0,
    clicked: 0, bounced: 0, blocked: 0, reply: 0,
  },
  data: [],

  // LinkedIn contacts
  linkedinData: [],
  linkedinTotal: 0,
  linkedinLoading: false,
  linkedinToEdit: [],

  // Telecaller contacts
  telecallerData: [],
  telecallerTotal: 0,
  telecallerLoading: false,
  telecallerToEdit: [],

  // Populated when a delete is refused with 409 so the page can open the
  // reassign dialog.
  reassignUsageData: null,

  // Addresses
  shippingAddressData: [],
  billingAddressData: [],
  shippingToEdit: [],
  billingToEdit: [],

  // Reference data
  countryCodeOptions: [],
  telecallerLocationOptions: [],
  linkedinLocationOptions: [],
  templateUrls: { allContacts: '', address: '', clients: '' },

  pageLoading: false,
  error: null,
};

const targetCompanySlice = createSlice({
  name: 'targetCompany',
  initialState,
  reducers: {
    setTargetCompanyField: (state, action) => {
      const { name, value } = action.payload;
      state.company = { ...state.company, [name]: value };
    },
    setTargetCompany: (state, action) => {
      state.company = action.payload || {};
    },
    // Let the inline table editors mutate the fetched rows before saving.
    setLinkedinList: (state, action) => {
      state.linkedinData = action.payload;
    },
    setTelecallerList: (state, action) => {
      state.telecallerData = action.payload;
    },
    setLinkedinToEdit: (state, action) => {
      state.linkedinToEdit = action.payload;
    },
    setTelecallerToEdit: (state, action) => {
      state.telecallerToEdit = action.payload;
    },
    setTargetReassignUsageData: (state, action) => {
      state.reassignUsageData = action.payload;
    },
    clearTargetReassignUsageData: (state) => {
      state.reassignUsageData = null;
    },
    setTargetShippingToEdit: (state, action) => {
      state.shippingToEdit = action.payload;
    },
    setTargetBillingToEdit: (state, action) => {
      state.billingToEdit = action.payload;
    },
    setTelecallerLocationOptions: (state, action) => {
      state.telecallerLocationOptions = action.payload;
    },
    setLinkedinLocationOptions: (state, action) => {
      state.linkedinLocationOptions = action.payload;
    },
    setTargetPageLoading: (state, action) => {
      state.pageLoading = action.payload;
    },
    clearTargetCompanyError: (state) => {
      state.error = null;
      state.companyError = null;
    },
    resetTargetCompanyState: () => initialState,
  },
  extraReducers: (builder) => {
    builder
      .addCase(fetchTargetCompany.pending, (state) => {
        state.companyLoading = true;
        state.companyError = null;
      })
      .addCase(fetchTargetCompany.fulfilled, (state, action) => {
        state.companyLoading = false;
        state.company = action.payload;
      })
      .addCase(fetchTargetCompany.rejected, (state, action) => {
        state.companyLoading = false;
        state.companyError = action.payload;
      })
      .addCase(fetchEmailStats.fulfilled, (state, action) => {
        state.emailStats = action.payload;
      })
      .addCase(loadTargetCompanyPage.pending, (state) => {
        state.pageLoading = true;
      })
      .addCase(loadTargetCompanyPage.fulfilled, (state) => {
        state.pageLoading = false;
      })
      .addCase(loadTargetCompanyPage.rejected, (state) => {
        state.pageLoading = false;
      })
      .addCase(fetchTargetData.fulfilled, (state, action) => {
        state.data = action.payload;
      })

      // LinkedIn
      .addCase(fetchLinkedinData.pending, (state) => {
        state.linkedinLoading = true;
      })
      .addCase(fetchLinkedinData.fulfilled, (state, action) => {
        state.linkedinLoading = false;
        state.linkedinData = action.payload.data;
        state.linkedinTotal = action.payload.total;
      })
      .addCase(fetchLinkedinData.rejected, (state, action) => {
        state.linkedinLoading = false;
        state.linkedinData = [];
        state.linkedinTotal = 0;
        state.error = action.payload;
      })
      .addCase(searchLinkedinData.fulfilled, (state, action) => {
        state.linkedinData = action.payload.data;
        state.linkedinTotal = action.payload.total;
      })
      .addCase(searchLinkedinData.rejected, (state) => {
        state.linkedinData = [];
        state.linkedinTotal = 0;
      })
      .addCase(fetchLinkedinToEdit.fulfilled, (state, action) => {
        state.linkedinToEdit = action.payload;
      })
      .addCase(deleteLinkedinContact.fulfilled, (state, action) => {
        state.linkedinData = state.linkedinData.filter(
          (contact) => contact.targetlinkedindetails_id !== action.payload
        );
      })
      .addCase(deleteLinkedinContact.rejected, (state, action) => {
        if (action.payload?.conflict) {
          state.reassignUsageData = action.payload;
        } else {
          state.error = action.payload?.message;
        }
      })

      // Telecaller
      .addCase(fetchTelecallerData.pending, (state) => {
        state.telecallerLoading = true;
      })
      .addCase(fetchTelecallerData.fulfilled, (state, action) => {
        state.telecallerLoading = false;
        state.telecallerData = action.payload.data;
        state.telecallerTotal = action.payload.total;
      })
      .addCase(fetchTelecallerData.rejected, (state, action) => {
        state.telecallerLoading = false;
        state.telecallerData = [];
        state.telecallerTotal = 0;
        state.error = action.payload;
      })
      .addCase(searchTelecallerData.fulfilled, (state, action) => {
        state.telecallerData = action.payload.data;
        state.telecallerTotal = action.payload.total;
      })
      .addCase(searchTelecallerData.rejected, (state) => {
        state.telecallerData = [];
        state.telecallerTotal = 0;
      })
      .addCase(fetchTelecallerToEdit.fulfilled, (state, action) => {
        state.telecallerToEdit = action.payload;
      })
      .addCase(deleteTelecallerContact.fulfilled, (state, action) => {
        state.telecallerData = state.telecallerData.filter(
          (contact) => contact.targettelecallerdetails_id !== action.payload
        );
      })
      .addCase(deleteTelecallerContact.rejected, (state, action) => {
        if (action.payload?.conflict) {
          state.reassignUsageData = action.payload;
        } else {
          state.error = action.payload?.message;
        }
      })

      // Addresses
      .addCase(fetchTargetShippingData.fulfilled, (state, action) => {
        state.shippingAddressData = action.payload;
      })
      .addCase(fetchTargetBillingData.fulfilled, (state, action) => {
        state.billingAddressData = action.payload;
      })
      .addCase(fetchTargetShippingToEdit.fulfilled, (state, action) => {
        state.shippingToEdit = action.payload;
      })
      .addCase(fetchTargetBillingToEdit.fulfilled, (state, action) => {
        state.billingToEdit = action.payload;
      })

      // Reference data
      .addCase(fetchTargetCountryCodeOptions.fulfilled, (state, action) => {
        state.countryCodeOptions = action.payload;
      })
      .addCase(fetchTargetTemplateUrl.fulfilled, (state, action) => {
        state.templateUrls[action.payload.kind] = action.payload.url;
      });
  },
});

export const {
  setTargetCompanyField,
  setTargetCompany,
  setLinkedinList,
  setTelecallerList,
  setLinkedinToEdit,
  setTelecallerToEdit,
  setTargetReassignUsageData,
  clearTargetReassignUsageData,
  setTargetShippingToEdit,
  setTargetBillingToEdit,
  setTelecallerLocationOptions,
  setLinkedinLocationOptions,
  setTargetPageLoading,
  clearTargetCompanyError,
  resetTargetCompanyState,
} = targetCompanySlice.actions;

export default targetCompanySlice.reducer;
